import { useEffect, useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEEPLINK_ACTION,
  DEEPLINK_QUERY_PARAM,
  SHARE_CODE_QUERY_PARAM,
  isDeeplinkAction,
  sanitizeShareCode,
} from "@/shared/config/deeplink";
import { setPendingDeeplink, setPendingShareCode } from "@/shared/model/deeplinkStore";

/**
 * 알림톡 딥링크(`?deeplink=exam_open` 등) 전역 캡처 인터셉터.
 *
 * `/main` 은 access=`"authenticated"` 라 미인증 콜드부트에서는 AuthGuard 가
 * `<Navigate to={LOGIN} replace />` 로 쿼리를 떨군다. 이 컴포넌트는 `<BrowserRouter>` 직속
 * sibling(AuthGuard 밖)에 위치해, 리다이렉트 이전에 의도를 저장한다.
 *
 * effect 를 둘로 나눈 이유:
 * - 의도 저장은 `useLayoutEffect` — store 직접 호출이라 라우터 상태와 무관하고, paint 전(passive effect 전)에
 *   커밋되어 React Router `<Navigate>`(passive `useEffect`)의 리다이렉트보다 항상 먼저다.
 * - URL 정리(`setSearchParams` replace)는 passive `useEffect` — `BrowserRouter` 는 history 리스너를 자기
 *   layout effect 에서 붙이는데, 자식의 layout effect 는 그보다 먼저 돌아 첫 마운트의 replace 가 window.history
 *   만 바꾸고 React 의 location 상태는 쿼리·옛 key(`default`) 그대로 남았다(#458 — TabShell 이 stale key 로
 *   더보기 scrollTop 을 저장해 뒤로가기 복원이 빠짐). passive 시점엔 리스너가 붙어 있어 상태가 따라온다.
 *   정리도 리다이렉트보다 먼저다 — 이 컴포넌트가 라우트 subtree(AuthGuard)보다 앞 형제라 passive effect 순서도 먼저.
 *
 * 알려진 action 만 저장하고(`isDeeplinkAction`), 제거하는 파라미터는 `deeplink`(알려진 값일 때)와 `share_code`
 * 뿐이다(다른 쿼리는 OAuth `?code=` 등과 공존할 수 있으므로 보존). 두 effect 는 같은 `searchParams` 를 각자 판정한다(ref 로 넘기지 않음 —
 * StrictMode 이중 호출·정리 뒤 재실행에서 상태가 꼬이지 않게). 정리 뒤 재실행은 지울 파라미터가 없어 즉시 끝난다.
 */
export function DeepLinkCapture() {
  const [searchParams, setSearchParams] = useSearchParams();

  // ① 의도 저장 — paint 전. 저장이 URL strip·리다이렉트보다 선행한다.
  useLayoutEffect(() => {
    const raw = searchParams.get(DEEPLINK_QUERY_PARAM);
    if (!isDeeplinkAction(raw)) return;

    setPendingDeeplink(raw);

    // 초대 등록 진입일 때만 공유 코드를 흡수 — 검증 실패 값은 저장하지 않는다
    // (수동 입력 폴백). 비식별 코드지만 값 로깅은 여전히 하지 않는다.
    if (raw === DEEPLINK_ACTION.INVITE_CODE_REGISTER) {
      const code = sanitizeShareCode(searchParams.get(SHARE_CODE_QUERY_PARAM));
      if (code) setPendingShareCode(code);
    }
  }, [searchParams]);

  // ② URL 정리 — passive. BrowserRouter 가 history 리스너를 붙인 뒤여야 replace 가 React location 에도 반영된다.
  useEffect(() => {
    const raw = searchParams.get(DEEPLINK_QUERY_PARAM);
    const hasDeeplink = isDeeplinkAction(raw);
    const hasShareCode = searchParams.has(SHARE_CODE_QUERY_PARAM);
    // 미지 deeplink 값은 지우지 않는다. 유효한 딥링크 없이 share_code 만 남은 경우에도 URL 은 정리한다(값은 미사용).
    if (!hasDeeplink && !hasShareCode) return;

    const next = new URLSearchParams(searchParams);
    if (hasDeeplink) next.delete(DEEPLINK_QUERY_PARAM);
    next.delete(SHARE_CODE_QUERY_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return null;
}

export default DeepLinkCapture;
